import type { Session } from "@supabase/supabase-js";
import { CalendarPlus, Eye, Mail, Plus, Send, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { appConfig } from "../lib/config";
import { api } from "../lib/api";
import {
  renderLessonInvitationPreview,
  sendLessonInvitation,
  type LessonInvitationInput,
  type LessonInvitationPreview,
} from "../lib/lessonInvitations";

type DraftLesson = {
  start: string;
  end: string;
};

export interface InvitationDraft {
  studentName: string;
  recipientEmail: string;
  lessons: DraftLesson[];
  teamsUrl: string;
  whiteboardUrl: string;
  lessonNote: string;
}

interface InvitationStudent {
  studentName: string;
  recipientEmail: string;
  teamsUrl?: string;
  whiteboardUrl?: string;
  lessonNote?: string;
}

function defaultLesson(): DraftLesson {
  const start = new Date();
  start.setDate(start.getDate() + 1);
  start.setMinutes(0, 0, 0);
  start.setHours(Math.max(10, start.getHours() + 1));
  const end = new Date(start.getTime() + 60 * 60_000);
  const local = (date: Date) => {
    const offset = date.getTimezoneOffset();
    return new Date(date.getTime() - offset * 60_000).toISOString().slice(0, 16);
  };
  return { start: local(start), end: local(end) };
}

export function createInvitationDraft(): InvitationDraft {
  return { studentName: "", recipientEmail: "", lessons: [defaultLesson()], teamsUrl: "", whiteboardUrl: "", lessonNote: "" };
}

function toIso(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error("Bitte prüfe Datum und Uhrzeit.");
  return date.toISOString();
}

export function AdminInvitationComposer({ session, draft, setDraft }: { session: Session; draft: InvitationDraft; setDraft: Dispatch<SetStateAction<InvitationDraft>> }) {
  const { studentName, recipientEmail, lessons, teamsUrl, whiteboardUrl, lessonNote } = draft;
  const [students, setStudents] = useState<InvitationStudent[]>([]);
  const [showStudents, setShowStudents] = useState(false);
  const [preview, setPreview] = useState<LessonInvitationPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [removingStudent, setRemovingStudent] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const calendarLabel = useMemo(() => lessons.length === 1 ? "1 Termin" : `${lessons.length} Termine`, [lessons.length]);
  const matchingStudents = useMemo(() => students.filter((student) => student.studentName.toLocaleLowerCase("de-DE").includes(studentName.trim().toLocaleLowerCase("de-DE"))).slice(0, 8), [students, studentName]);

  useEffect(() => {
    let active = true;
    void api.adminAction<{ students: InvitationStudent[] }>(session, "invitationStudents")
      .then((result) => { if (active) setStudents(result.students); })
      .catch(() => { /* Suggestions are optional; sending remains available. */ });
    return () => { active = false; };
  }, [session]);

  function updateDraft(patch: Partial<InvitationDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  function chooseStudent(student: InvitationStudent) {
    updateDraft({ studentName: student.studentName, recipientEmail: student.recipientEmail, teamsUrl: student.teamsUrl ?? "", whiteboardUrl: student.whiteboardUrl ?? "", lessonNote: student.lessonNote ?? "" });
    setShowStudents(false);
  }

  async function removeStudent(student: InvitationStudent) {
    setRemovingStudent(student.studentName);
    setError("");
    try {
      await api.adminAction(session, "hideInvitationStudent", { studentName: student.studentName });
      setStudents((current) => current.filter((item) => item.studentName.toLocaleLowerCase("de-DE") !== student.studentName.toLocaleLowerCase("de-DE")));
      setMessage(`„${student.studentName}“ wurde aus den Vorschlägen entfernt.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Der Vorschlag konnte nicht entfernt werden.");
    } finally {
      setRemovingStudent(null);
    }
  }

  function updateLesson(index: number, patch: Partial<DraftLesson>) {
    setDraft((current) => ({ ...current, lessons: current.lessons.map((lesson, itemIndex) => itemIndex === index ? { ...lesson, ...patch } : lesson) }));
  }

  function buildInput(): LessonInvitationInput {
    if (!studentName.trim()) throw new Error("Bitte trage den Namen des Schülers ein.");
    if (!recipientEmail.trim()) throw new Error("Bitte trage eine Empfänger-E-Mail ein.");
    if (!teamsUrl.trim() && !whiteboardUrl.trim()) throw new Error("Bitte trage mindestens Teams oder Whiteboard ein.");
    return {
      recipientEmail: recipientEmail.trim(),
      studentName: studentName.trim(),
      lessons: lessons.map((lesson) => ({ start: toIso(lesson.start), end: toIso(lesson.end) })),
      teamsUrl: teamsUrl.trim() || undefined,
      whiteboardUrl: whiteboardUrl.trim() || undefined,
      lessonNote: lessonNote.trim() || undefined,
    };
  }

  async function run(previewOnly: boolean) {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const input = buildInput();
      if (previewOnly) {
        setPreview(renderLessonInvitationPreview(input));
        setMessage("Vorschau wurde lokal erzeugt.");
        return;
      }

      const result = await sendLessonInvitation(session, input);
      if (result.preview) setPreview(result.preview);
      if (result.sent) {
        setStudents((current) => [{ studentName: input.studentName, recipientEmail: input.recipientEmail, teamsUrl: input.teamsUrl, whiteboardUrl: input.whiteboardUrl, lessonNote: input.lessonNote }, ...current.filter((student) => student.studentName.toLocaleLowerCase("de-DE") !== input.studentName.toLocaleLowerCase("de-DE"))]);
        const created = result.calendarEventsCreated ?? 0;
        const reused = result.calendarEventsAlreadyExisting ?? 0;
        setMessage(`Einladung wurde versendet. ${created} Kalendereintrag${created === 1 ? "" : "e"} neu angelegt${reused ? `, ${reused} bereits vorhanden` : ""}.`);
      } else if (result.staging) {
        setMessage("Staging-Test erfolgreich. Es wurden bewusst keine echten Kalendereinträge und keine E-Mail erzeugt.");
      } else {
        setMessage("Einladung wurde verarbeitet.");
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Einladung konnte nicht verarbeitet werden.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="settings-stack">
      <section className="admin-panel">
        <div className="panel-head">
          <div><h2>Unterrichtseinladung erstellen</h2><p>{calendarLabel} · Kalender + HTML-Mail in einem Schritt.</p></div>
          <span className="integration-icon connected"><Mail /></span>
        </div>
        {appConfig.stagingMode && <div className="alert alert-warning">🧪 Staging-Modus: Vorschau und Backend werden getestet. Es werden keine echten Kalendereinträge und keine E-Mail erzeugt.</div>}
        {message && <div className="alert alert-success">✓ {message}</div>}
        {error && <div className="alert alert-error">⚠ {error}</div>}
        <div className="admin-edit-grid">
          <div className="invitation-student-field" onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setShowStudents(false); }}>
            <label htmlFor="invitation-student-name">Schülername</label>
            <input id="invitation-student-name" value={studentName} onFocus={() => setShowStudents(true)} onChange={(event) => { updateDraft({ studentName: event.target.value }); setShowStudents(true); }} placeholder="z. B. Yunu" autoComplete="off" />
            {showStudents && matchingStudents.length > 0 && <div className="invitation-student-suggestions" role="group" aria-label="Bisherige Schüler">
              {matchingStudents.map((student) => <div className="invitation-student-option" key={`${student.studentName}:${student.recipientEmail}`}>
                <button className="invitation-student-select" type="button" onClick={() => chooseStudent(student)}><strong>{student.studentName}</strong><small>{student.recipientEmail}</small></button>
                <button className="invitation-student-remove" type="button" title="Aus Vorschlägen entfernen" aria-label={`Vorschlag für ${student.studentName} entfernen`} disabled={removingStudent === student.studentName} onClick={() => void removeStudent(student)}><Trash2 /></button>
              </div>)}
            </div>}
          </div>
          <label>Empfänger-E-Mail<input type="email" value={recipientEmail} onChange={(event) => updateDraft({ recipientEmail: event.target.value })} placeholder="name@example.com" /></label>
        </div>
      </section>

      <section className="admin-panel">
        <div className="panel-head">
          <div><h2>Termine</h2><p>Beim echten Versand wird für jeden Termin automatisch ein Google-Calendar-Eintrag erstellt und der Empfänger als Teilnehmer eingeladen.</p></div>
          <button className="button button-secondary button-small" type="button" onClick={() => setDraft((current) => ({ ...current, lessons: [...current.lessons, defaultLesson()] }))}><Plus /> Termin</button>
        </div>
        <div className="schedule-list">
          {lessons.map((lesson, index) => (
            <div className="schedule-row" key={index} style={{ gridTemplateColumns: "1fr 1fr auto" }}>
              <label style={{ display: "grid", gap: 6 }}><small>Start</small><input type="datetime-local" value={lesson.start} onChange={(event) => updateLesson(index, { start: event.target.value })} /></label>
              <label style={{ display: "grid", gap: 6 }}><small>Ende</small><input type="datetime-local" value={lesson.end} onChange={(event) => updateLesson(index, { end: event.target.value })} /></label>
              <button className="icon-button danger" type="button" aria-label="Termin entfernen" disabled={lessons.length === 1} onClick={() => setDraft((current) => ({ ...current, lessons: current.lessons.filter((_, itemIndex) => itemIndex !== index) }))}><Trash2 /></button>
            </div>
          ))}
        </div>
      </section>

      <section className="admin-panel">
        <div className="panel-head"><div><h2>Unterrichtslinks</h2><p>Teams und Whiteboard erscheinen sowohl in der HTML-Mail als auch automatisch in der Kalenderbeschreibung.</p></div><CalendarPlus /></div>
        <div className="admin-edit-grid">
          <label>Teams-Link <em>optional</em><input value={teamsUrl} onChange={(event) => updateDraft({ teamsUrl: event.target.value })} placeholder="https://teams.live.com/..." /></label>
          <label>Whiteboard-Link <em>optional</em><input value={whiteboardUrl} onChange={(event) => updateDraft({ whiteboardUrl: event.target.value })} placeholder="https://www.tldraw.com/f/..." /></label>
        </div>
        <label style={{ display: "grid", gap: 7, marginTop: 16 }}>Hinweis für die Stunde <em>optional</em><textarea rows={3} value={lessonNote} onChange={(event) => updateDraft({ lessonNote: event.target.value })} placeholder="z. B. aktuelle Hausaufgaben bereithalten" /></label>
        <div className="admin-actions" style={{ marginTop: 18 }}>
          <button className="button button-secondary" type="button" disabled={busy} onClick={() => void run(true)}><Eye /> {busy ? "Wird erstellt …" : "Vorschau erzeugen"}</button>
          <button className="button button-primary" type="button" disabled={busy} onClick={() => void run(false)}><Send /> {appConfig.stagingMode ? "Staging-Test ausführen" : "Einladung senden"}</button>
        </div>
      </section>

      {preview && (
        <section className="admin-panel">
          <div className="panel-head"><div><h2>E-Mail-Vorschau</h2><p>{preview.subject}</p></div></div>
          <iframe title="E-Mail-Vorschau" srcDoc={preview.html} style={{ width: "100%", height: 720, border: "1px solid #dfe5ef", borderRadius: 14, background: "#f7f8fb" }} />
        </section>
      )}
    </div>
  );
}
